// Global
import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';
import NextLink from 'next/link';
import { tv } from 'tailwind-variants';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

// Local
import useClickOutside from 'lib/hooks/useClickOutside';
import useDictionary from 'lib/hooks/useDictionary';

import { useRealPathName } from 'lib/hooks/useRealPathName';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';

import { US, AE, MX, CA } from 'country-flag-icons/react/3x2';
const Flags = { US, AE, MX, CA };

// Alternate method to dynamically include all flags, however it's recommended to only include
// necessary ones to reduce bundle size.
// import * as Flags from 'country-flag-icons/react/3x2';

type FlagNames = keyof typeof Flags;

const LanguageSelector = () => {
  const router = useRouter();

  const locale = router.locale;

  // Don't use from router because that includes rewrites
  const pathname = useRealPathName();

  // Don't use `router.query` because that includes the wildcard JSS route "path".
  const query = router.asPath.split('?')[1]?.split('#')[0];

  const selectRef = useRef<HTMLDivElement>(null);

  const { getDictionaryValue } = useDictionary();

  const sitecoreContext = useSitecoreContext();

  const languages = sitecoreContext.sitecoreContext.languages ?? [];

  const currentLanguage = languages.find((x) => x.isoCode === locale);

  // Flag component based off country code
  const CurrentFlag = Flags[currentLanguage?.countryCode as FlagNames];

  /*
   * State
   */

  const [dropdownOpen, setDropdownOpen] = useState(false);

  /*
   * Event Handlers
   */

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setDropdownOpen((dropdownOpen) => !dropdownOpen);
    }
  }, []);

  const handleBlur = useCallback((event: React.FocusEvent) => {
    // Check if the new focus target is outside the dropdown
    if (!selectRef.current?.contains(event.relatedTarget as Node)) {
      setDropdownOpen(false);
    }
  }, []);

  /*
   * Lifecycle
   */

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  useOnRouteChange(closeDropdown);

  useClickOutside(selectRef, dropdownOpen, closeDropdown);

  /*
   * Rendering
   */

  const {
    base,
    buttonClasses,
    imageContainer,
    countryNameWrapper,
    dropDownMenuWrapper,
    dropDownMenuList,
    dropDownImageWrapper,
    dropDownItemName,
    instructions,
  } = TAILWIND_VARIANTS();
  return (
    <div className={base()} ref={selectRef} onBlur={handleBlur}>
      <div className={instructions()}>{getDictionaryValue('CountrySelectorInstructions')}</div>
      <button
        aria-expanded={dropdownOpen}
        aria-haspopup="listbox"
        aria-label={getDictionaryValue('CountrySelectorAriaLabel', 'Select A Country')}
        className={buttonClasses()}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        onKeyDown={handleKeyDown}
        type="button"
      >
        <div className={imageContainer()}>
          <CurrentFlag width={20} />
          <div className={countryNameWrapper()}>
            {currentLanguage?.nativeName}{' '}
            <span className="sr-only">{getDictionaryValue('IsSelected', 'is selected')}</span>
          </div>
        </div>
      </button>
      {dropdownOpen ? (
        <div className={dropDownMenuWrapper()} role="listbox">
          <ul className={dropDownMenuList()}>
            {languages?.map((item) => {
              const isSelected = currentLanguage?.isoCode === item?.isoCode;
              const { dropDownMenuItem } = TAILWIND_VARIANTS({
                isSelected,
              });

              const CurrentFlag = Flags[item.countryCode as FlagNames];

              return (
                <li
                  aria-selected={isSelected}
                  className={dropDownMenuItem()}
                  key={item.isoCode}
                  role="option"
                >
                  {/* We directly use NextLink here so we can change the `locale` and have NextJS handle it. */}
                  <NextLink
                    href={{ pathname: pathname, query: query }}
                    locale={item.isoCode}
                    className={dropDownImageWrapper()}
                    aria-label={
                      isSelected
                        ? `${item.nativeName} ${getDictionaryValue('IsSelected', 'is selected')}`
                        : `${item.nativeName}`
                    }
                  >
                    <CurrentFlag width={20} />
                    <span className={dropDownItemName()}>{item?.nativeName}</span>
                  </NextLink>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default LanguageSelector;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['block', 'relative', 'text-left', 'md:mr-4'],
    buttonClasses: [
      'block',
      'border-gray-300',
      'cursor-pointer',
      'rounded-md',
      'text-base',
      'w-full',
      'focus:border-none',
      'focus:outline-theme-brand3',
      'md:p-xxs',
      'sm:text-sm',
    ],
    countryNameWrapper: ['ml-3', 'truncate', 'lg:block', 'capitalize'],
    dropDownImageWrapper: [
      'flex',
      'items-center',
      'w-full',
      'py-2',
      'pl-4',
      'pr-8',
      'focus:outline-theme-brand3',
    ],
    dropDownItemName: ['block', 'font-semibold', 'ml-3', 'capitalize'],
    dropDownMenuItem: [
      'cursor-pointer',
      'list-none',
      'relative',
      'select-none',
      'hover:text-slate-500',
    ],
    dropDownMenuList: [
      'm-0',
      'max-h-60',
      'overflow-auto',
      'py-1',
      'text-base',
      'focus:outline-none',
      'sm:text-sm',
      'lg:w-max',
    ],
    dropDownMenuWrapper: [
      'absolute',
      'bg-white',
      'mt-1',
      'rounded-md',
      'shadow-lg',
      'w-full',
      'z-10',
      'md:w-auto',
    ],
    imageContainer: ['flex', 'items-center'],
    instructions: ['font-bold', 'text-left', 'md:hidden'],
  },
  variants: {
    isSelected: {
      false: {
        dropDownMenuItem: [],
      },
      true: {
        dropDownMenuItem: ['bg-theme-black-300'],
      },
    },
  },
});
